import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customersApi, productsApi } from '../api';
import { useAuthStore } from '../store/auth';
import { useToast } from '../components/ui/ToastProvider';
import {
  CustomerPicker,
  PaymentPanel,
  ProductPicker,
  ReceiptView,
  formatUzs,
  usePosCart,
  usePosCheckout,
} from '../features/pos';
import type { ConfirmedSale } from '../features/pos';
import type { PosCustomer, PosProduct } from '../features/pos';
import type { SaleResult } from '../types';

type Step = 'products' | 'customer' | 'payment' | 'receipt';

const NO_PRODUCTS: PosProduct[] = [];
const NO_CUSTOMERS: PosCustomer[] = [];

/**
 * Sotuv ekrani uchun butun katalog — bitta so'rovda (`/products/pos/`).
 *
 * Tovarlar bir marta yuklanadi va qidiruv brauzerda ishlaydi. Ilgari
 * 100 talik sahifalar bilan olinardi: 486 tovar 5 ta so'rov va serverga
 * ikki marta borib-kelish edi, butun katalog esa atigi ~75 KB.
 */
async function fetchPosProducts(): Promise<PosProduct[]> {
  const res = await productsApi.pos();
  // Narxlar DRF'dan satr bo'lib keladi ("150000.00"). Songa o'tkazmasak
  // `toLocaleString` ularni o'sha holicha chiqaradi va sotuvchi
  // "150000.00 so'm" ko'radi.
  return res.data.results.map((p) => ({
    id: p.id,
    name: p.name,
    sell_price_uzs: Number(p.sell_price_uzs) || 0,
    cost_uzs: p.cost_uzs != null ? Number(p.cost_uzs) : undefined,
    stock: Number(p.stock) || 0,
    category_name: p.category_name,
  }));
}

async function fetchPosCustomers(): Promise<PosCustomer[]> {
  const res = await customersApi.list({ page_size: 100 });
  return res.data.results.map((c) => ({
    id: c.id,
    name: c.name,
    phone: c.phone,
    debt: Number(c.debt) || 0,
  }));
}

/**
 * Sotuv (POS) — `/orders/new`.
 *
 * Oqim Telegram Mini App'dagi bilan bir xil: tovar → mijoz → to'lov → chek.
 * Farqi faqat joylashuvda — keng ekranda savat tovarlar yonida turadi,
 * torida esa qadamma-qadam ochiladi. Mantiq `features/pos/` da, shuning uchun
 * ikkala yuza ham bitta manbadan ishlaydi.
 */
export const NewOrder: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { permissions } = useAuthStore();

  const canEditPrices = permissions.can_edit_prices;

  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState<PosCustomer | null>(null);
  const [result, setResult] = useState<SaleResult | null>(null);

  // Ro'yxatlar react-query keshida turadi: Sotuv ekraniga qayta kirilganda
  // oldingi ro'yxat DARHOL ko'rinadi, yangisi esa fonda so'raladi (qoldiq
  // o'zgargan bo'lsa jimgina yangilanadi). Kalitda foydalanuvchi bor — boshqa
  // hisobga kirilganda birovning katalogi ko'rinib qolmasin.
  const userId = useAuthStore((s) => s.user?.id);
  const productsQuery = useQuery({
    queryKey: ['pos', 'products', userId],
    queryFn: fetchPosProducts,
    gcTime: 30 * 60 * 1000,
  });
  const customersQuery = useQuery({
    queryKey: ['pos', 'customers', userId],
    queryFn: fetchPosCustomers,
    gcTime: 30 * 60 * 1000,
  });
  const products = productsQuery.data ?? NO_PRODUCTS;
  const customers = customersQuery.data ?? NO_CUSTOMERS;

  useEffect(() => {
    if (productsQuery.isError) toast.error('Tovarlarni yuklab bo\'lmadi.');
  }, [productsQuery.isError, toast]);
  useEffect(() => {
    if (customersQuery.isError) toast.error('Mijozlarni yuklab bo\'lmadi.');
  }, [customersQuery.isError, toast]);

  const cart = usePosCart(products, canEditPrices);
  const checkout = usePosCheckout();

  // Qadam brauzer tarixida (`?step=`) turadi. Faqat React holatida bo'lsa,
  // telefonning "orqaga" tugmasi to'lovdan oldingi qadamga emas, Sotuvdan
  // oldin ochilgan sahifaga (masalan, Kategoriyalar) otib yuborardi.
  //
  // URL'dagi qadam faqat uning sharti bajarilsa ko'rsatiladi: yangilangan
  // sahifada savat bo'sh bo'ladi va `?step=payment` baribir tovarlarga tushadi.
  const requested = searchParams.get('step') as Step | null;
  const step: Step =
    requested === 'receipt' && result ? 'receipt'
    : requested === 'payment' && customer && cart.count > 0 ? 'payment'
    : requested === 'customer' && cart.count > 0 ? 'customer'
    : 'products';

  const setStep = useCallback(
    (next: Step, opts?: { replace?: boolean }) => {
      navigate({ search: next === 'products' ? '' : `?step=${next}` }, opts);
    },
    [navigate],
  );

  const handleConfirm = useCallback(
    async (sale: ConfirmedSale) => {
      if (!customer) return;

      const saved = await checkout.submit({
        items: cart.lines.map((line) => ({
          product_id: line.product.id,
          quantity: line.quantity,
          // Narx faqat o'zgartirilgan bo'lsa yuboriladi.
          ...(line.isDiscounted ? { price: line.price } : {}),
        })),
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        paid_amount: sale.totals.paying,
        payment_method: sale.totals.paying === 0 ? 'debt' : sale.method,
        due_date: sale.dueDate,
      });

      if (saved) {
        setResult(saved);
        // To'lov qadami tarixdan olib tashlanadi — "orqaga" bilan unga
        // qaytib, o'sha sotuvni qayta yuborib bo'lmasin.
        setStep('receipt', { replace: true });
      }
    },
    [cart.lines, checkout, customer, setStep],
  );

  const resetSale = useCallback(() => {
    cart.reset();
    checkout.reset();
    setCustomer(null);
    setResult(null);
    // Sotuvdan keyin qoldiq va mijoz qarzi o'zgargan — yangilab olamiz.
    productsQuery.refetch();
    customersQuery.refetch();
  }, [cart, checkout, productsQuery, customersQuery]);

  const startNewSale = useCallback(() => {
    resetSale();
    setStep('products', { replace: true });
  }, [resetSale, setStep]);

  // Chekdan telefonning "orqaga" tugmasi bilan chiqilsa — sotuv yakunlangan,
  // savat bilan eski qadamga qaytish o'rniga yangi sotuv boshlanadi.
  // Faqat `receipt` dan CHIQISH hisoblanadi: sotuv saqlangan zahoti natija
  // va URL bir renderda yangilanmasligi mumkin, o'shanda chek yo'qolmasin.
  const prevRequested = useRef(requested);
  useEffect(() => {
    const wasReceipt = prevRequested.current === 'receipt';
    prevRequested.current = requested;
    if (wasReceipt && requested !== 'receipt') resetSale();
  }, [requested, resetSale]);

  // Ekrandagi "orqaga" tugmasi ham tarix bo'yicha yuradi — telefon tugmasi
  // bilan bir xil ishlashi uchun.
  const back = useCallback(() => {
    if (step === 'products') navigate('/orders');
    else navigate(-1);
  }, [navigate, step]);

  /**
   * Savat paneli — faqat keng ekranda va faqat tovar tanlanganda.
   * Bo'sh savat kartasi hech qanday ma'lumot bermaydi, o'chiq tugma esa
   * yo'lni to'sib turgandek ko'rinadi.
   */
  const cartPanel = cart.count === 0 ? null : (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-gray-900">Savat</span>
        <span className="pill pill-neutral tabular-nums">{cart.count} ta</span>
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto">
        {cart.lines.map((line) => (
          <div key={line.product.id} className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-gray-700 truncate">
              {line.product.name}
              <span className="text-gray-400"> × {line.quantity}</span>
            </span>
            <span className="font-medium text-gray-900 tabular-nums whitespace-nowrap">
              {formatUzs(line.subtotal)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-baseline justify-between pt-3 mt-3 border-t border-gray-100">
        <span className="font-semibold text-gray-900">Jami</span>
        <span className="text-lg font-bold text-gray-900 tabular-nums">
          {formatUzs(cart.subtotal)}
        </span>
      </div>

      {step === 'products' && (
        <button
          type="button"
          className="btn btn-primary w-full mt-3"
          onClick={() => setStep('customer')}
        >
          Davom etish
        </button>
      )}
    </div>
  );

  /**
   * Tor ekrandagi savat — bitta yashil chiziq: soni, summasi va davom etish
   * tugmasi. Barmoq yetadigan joyda va bo'sh savatda umuman chizilmaydi.
   */
  const cartBar = cart.count === 0 || step !== 'products' ? null : (
    <div className="lg:hidden fixed inset-x-0 bottom-[calc(60px+env(safe-area-inset-bottom,0px))] px-4 z-30">
      <button
        type="button"
        onClick={() => setStep('customer')}
        className="w-full flex items-center justify-between gap-3 rounded-xl bg-primary-600 px-4 py-3.5
          text-white shadow-lg shadow-primary-600/30 active:bg-primary-active"
      >
        <span className="flex items-center gap-2">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-white/25 text-sm font-bold tabular-nums">
            {cart.count}
          </span>
          <span className="font-semibold">Savat</span>
        </span>
        <span className="font-bold tabular-nums">{formatUzs(cart.subtotal)}</span>
      </button>
    </div>
  );

  // Savat bor bo'lsa o'ng ustun chiziladi, aks holda ro'yxat butun kenglikni oladi.
  const columns = cart.count > 0 ? 'lg:grid-cols-[1fr_320px]' : 'lg:grid-cols-1';

  return (
    <div>
      {checkout.error && (
        <div className="card p-4 mb-4 border-danger-600 bg-danger-50">
          <p className="text-sm text-danger-700">{checkout.error}</p>
        </div>
      )}

      {step === 'products' && (
        <div className={`grid gap-4 items-start ${columns}`}>
          <ProductPicker
            products={products}
            loading={productsQuery.isPending}
            cart={cart.cart}
            onStep={cart.step}
            onSetQuantity={cart.setQuantity}
            stockWarning={cart.stockWarning}
            onBack={back}
          />
          {cartPanel && <div className="hidden lg:block lg:sticky lg:top-4">{cartPanel}</div>}
        </div>
      )}

      {step === 'customer' && (
        <div className={`grid gap-4 items-start ${columns}`}>
          <CustomerPicker
            customers={customers}
            selected={customer}
            onSelect={(c) => { setCustomer(c); setStep('payment'); }}
            canCreate
            onBack={back}
          />
          {cartPanel && <div className="hidden lg:block lg:sticky lg:top-4">{cartPanel}</div>}
        </div>
      )}

      {step === 'payment' && customer && (
        <div className="max-w-2xl">
          <PaymentPanel
            lines={cart.lines}
            customer={customer}
            canEditPrices={canEditPrices}
            customPrices={cart.customPrices}
            priceErrors={cart.priceErrors}
            onPriceChange={(productId, raw) => cart.setPrice(productId, raw)}
            onConfirm={handleConfirm}
            submitting={checkout.submitting}
            onBack={back}
          />
        </div>
      )}

      {step === 'receipt' && result && (
        <ReceiptView result={result} onNewSale={startNewSale} />
      )}

      {cartBar}
    </div>
  );
};
